import type { ReactNode } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link } from '@tanstack/react-router';

import { calendarDay, clockTime, spanText, usdText } from '../../../shared/lib/format';
import { Button } from '../../../shared/ui/button';
import { AlertIcon, MergeIcon, WaitIcon, AiIcon } from '../../../shared/ui/icons';
import { MutationError } from '../../../shared/ui/mutation-error';
import { ErrorState, LoadingState } from '../../../shared/ui/states';
import { flowKeys, getVersion, parseGraph } from '../../../entities/flow';
import type { TIssueDetail } from '../../../entities/issue';
import { listRuns, stopRun } from '../../../entities/run';
import { getAiBudgetToday } from '../../../entities/organization';

const Card = ({
  icon,
  title,
  children,
  actions,
}: {
  icon: ReactNode;
  title: string;
  children: ReactNode;
  actions?: ReactNode;
}) => (
  <section className="flex items-start gap-3 rounded-panel border border-border-sunken px-input py-4">
    <span className="flex size-avatar shrink-0 items-center justify-center rounded-control bg-chip text-muted">
      {icon}
    </span>
    <div className="min-w-0 flex-1">
      <h2 className="text-data font-semibold text-primary">{title}</h2>
      <div className="mt-1 text-meta leading-18 text-muted">{children}</div>
    </div>
    {actions && <div className="flex shrink-0 items-center gap-2">{actions}</div>}
  </section>
);

const primaryLink =
  'inline-flex h-control-sm items-center rounded-control bg-accent px-input text-data font-semibold text-surface';

export const RateLimitCard = ({
  issue,
  destinationId,
  destinationName,
}: {
  issue: TIssueDetail;
  destinationId: string | null;
  destinationName: string | null;
}) => {
  const name = destinationName ?? 'the destination';
  return (
    <Card
      icon={<WaitIcon size={14} />}
      title="Slow down, don’t resend"
      actions={
        destinationId && (
          <Link
            to="/destinations/$destinationId"
            params={{ destinationId }}
            hash="rate-limit"
            className={primaryLink}
          >
            Cap {name}’s rate
          </Link>
        )
      }
    >
      429 means {name} wants fewer requests. Cap the destination’s rate and copies queue instead of
      failing; resending all {issue.eventCount.toLocaleString('en-US')} now would hit the same wall.
    </Card>
  );
};

export const MergeCard = ({ issue, flowId }: { issue: TIssueDetail; flowId: string }) => {
  const version = useQuery({
    queryKey: flowKeys.version(flowId, 'live'),
    queryFn: () => getVersion(flowId, 'live'),
  });
  const node = parseGraph(version.data?.graph ?? null)?.nodes.find(
    (each) => each.key === issue.node?.key,
  );
  const config = node?.config ?? {};
  const inputs = typeof config.inputs === 'number' ? config.inputs : null;
  const timeoutSec = typeof config.timeoutSec === 'number' ? config.timeoutSec : null;
  const mode = typeof config.mode === 'string' ? config.mode : null;
  const n = issue.eventCount.toLocaleString('en-US');

  return (
    <Card
      icon={<MergeIcon size={14} />}
      title={`${issue.node?.name ?? 'Merge'} gave up on ${n} ${issue.eventCount === 1 ? 'run' : 'runs'}`}
      actions={
        <Link
          to="/flows/$flowId"
          params={{ flowId }}
          search={issue.node ? { node: issue.node.key } : {}}
          className={primaryLink}
        >
          Change how long it waits
        </Link>
      }
    >
      {version.isPending && <LoadingState rows={1} />}
      {version.isError && (
        <ErrorState error={version.error} onRetry={() => void version.refetch()} />
      )}
      {inputs !== null && timeoutSec !== null && (
        <>
          It waits for {mode === 'all' ? `all ${inputs}` : `${inputs}`} inputs; after{' '}
          {spanText(timeoutSec * 1000)} without them it leaves by its error output. A branch whose
          retries last longer than that reaches it too late.
        </>
      )}
    </Card>
  );
};

export const ScheduleCard = ({
  issue,
  flowId,
  timeZone,
  now,
}: {
  issue: TIssueDetail;
  flowId: string;
  timeZone?: string;
  now: number;
}) => {
  const client = useQueryClient();
  const busy = useQuery({
    queryKey: ['runs', 'running', flowId],
    queryFn: () =>
      listRuns({
        flowId: [flowId],
        status: ['running'],
        from: new Date(now - 30 * 86_400_000).toISOString(),
        limit: 5,
      }),
  });
  const run = busy.data?.data.at(-1) ?? null;
  const stop = useMutation({
    mutationFn: (target: { id: string; at: string }) => stopRun(target.id, target.at),
    onSuccess: () => {
      void client.invalidateQueries({ queryKey: ['runs', 'running', flowId] });
      void client.invalidateQueries({ queryKey: ['issue', issue.id] });
    },
  });
  const started = run ? `${calendarDay(run.at, now, timeZone)} ${clockTime(run.at, timeZone)}` : '';

  return (
    <Card
      icon={<WaitIcon size={14} />}
      title={`${issue.flowName ?? 'This flow'} skipped ${issue.eventCount.toLocaleString('en-US')} ${issue.eventCount === 1 ? 'tick' : 'ticks'}`}
      actions={
        run && (
          <>
            <Button
              size="sm"
              waiting={stop.isPending}
              waitingLabel="Stopping"
              onClick={() => stop.mutate(run)}
            >
              Stop the {calendarDay(run.at, now, timeZone)} run
            </Button>
            <Link
              to="/events/runs/$runId"
              params={{ runId: run.id }}
              search={{ at: run.at }}
              className="text-meta text-primary underline"
            >
              Open the run
            </Link>
          </>
        )
      }
    >
      {busy.isPending && <LoadingState rows={1} />}
      {busy.isError && <ErrorState error={busy.error} onRetry={() => void busy.refetch()} />}
      {run && (
        <>
          The run from {started} is still going after {spanText(now - new Date(run.at).getTime())}
          {run.statusNode ? `, at ${run.statusNode.name}` : ''}.{' '}
        </>
      )}
      {busy.isSuccess && !run && 'No run of it is going now; the next tick starts one. '}
      The schedule skips a tick while the last run is busy, and after 3 skips it opens this issue.
      {stop.data && (
        <p className="mt-2 text-primary">
          Stopped: {stop.data.copies.toLocaleString('en-US')}{' '}
          {stop.data.copies === 1 ? 'copy' : 'copies'} left the run.
        </p>
      )}
      {stop.isError && <MutationError error={stop.error} />}
    </Card>
  );
};

export const AiBudgetCard = ({ issue, timeZone }: { issue: TIssueDetail; timeZone?: string }) => {
  const today = useQuery({ queryKey: ['ai-budget'], queryFn: getAiBudgetToday });
  const data = today.data;
  const step = issue.node?.name ?? 'This step';
  return (
    <Card
      icon={<AiIcon size={14} />}
      title={`${step} is holding copies`}
      actions={
        <Link to="/settings/storage" className={primaryLink}>
          Raise today’s budget
        </Link>
      }
    >
      {today.isPending && <LoadingState rows={1} />}
      {today.isError && <ErrorState error={today.error} onRetry={() => void today.refetch()} />}
      {data && (
        <>
          {data.limitUsd <= 0
            ? 'Today’s AI budget is $0.00, which turns AI steps off. '
            : `Today’s AI budget, ${usdText(data.limitUsd)}, is spent (${usdText(data.spentUsd)}). `}
          Copies wait on this step instead of being dropped.
          <ul className="mt-2 flex list-disc flex-col gap-1 pl-5">
            <li>
              {data.waiting.toLocaleString('en-US')}{' '}
              {data.waiting === 1 ? 'copy waits' : 'copies wait'} on an AI step
            </li>
            <li>starts again at {clockTime(data.resetsAt, timeZone)}</li>
          </ul>
        </>
      )}
    </Card>
  );
};

export const RemovedCard = ({
  issue,
  ranOn,
  onRedeliver,
}: {
  issue: TIssueDetail;
  /** The version the failed copies started on — what a redelivery runs through. */
  ranOn: number | null;
  onRedeliver: (() => void) | null;
}) => {
  const was = ranOn === null ? 'the version they started on' : `v${ranOn}`;
  return (
    <Card
      icon={<AlertIcon size={14} />}
      title={issue.nodeRemovedText ?? `This step was removed in v${issue.removedInVersion}`}
      actions={
        onRedeliver && (
          <Button size="sm" onClick={onRedeliver}>
            Redeliver on {was}…
          </Button>
        )
      }
    >
      The issue stays: {issue.eventCount.toLocaleString('en-US')}{' '}
      {issue.eventCount === 1 ? 'copy' : 'copies'} failed on it on {was}. Runs keep the version they
      started on, so redelivering them still goes through {was}.
    </Card>
  );
};
